import React, { useEffect, useMemo, useState } from "react"
import Select from "react-select"
import {
  Card, CardBody, Row, Col, Input, Table, Spinner, Button,
  Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label,
} from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { getFleetFuelReport, saveFleetFuelLog, removeFleetFuelLog, getVehicles, getFleetPaymentLedgers, postFleetFuelVoucher, postAllFleetFuelVouchers } from "../../services/fleet"
import { toastError, toastSuccess } from "../../helpers/msg_helper"
import useCollapsedSidebar from "../../hooks/useCollapsedSidebar"

const money = v => (v == null ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
const num = v => (v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }))
const fmtDate = s => (s ? String(s).slice(0, 10).split("-").reverse().join("-") : "—")
const thisMonth = () => new Date().toISOString().slice(0, 7)
const blankForm = () => ({ id: null, vehicle_id: "", date: new Date().toISOString().slice(0, 10), km: "", fuel_qty: "", amount: "", fuel_type: "diesel", odometer: "", station: "", driver_name: "", remarks: "", payment_ledger_id: "" })

/** Operations → Fleet → Fuel Consumption. Per-vehicle KM/qty/amount → mileage & cost/km, with
 *  petrol-vs-diesel and heavy-vs-light splits. Manual entry per fill. */
const FleetFuel = () => {
  useCollapsedSidebar()
  document.title = "Fuel Consumption | Calyvern"
  const [month, setMonth] = useState("2026-07")
  const [data, setData] = useState({ rows: [], logs: [], summary: {} })
  const [loading, setLoading] = useState(false)
  const [vehicles, setVehicles] = useState([])
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(blankForm())
  const [saving, setSaving] = useState(false)
  const [payLedgers, setPayLedgers] = useState([]) // common bank/cash ledgers ("Pay from")
  const [postFor, setPostFor] = useState(null) // fuel log we're posting a voucher for (2nd way)
  const [postLedger, setPostLedger] = useState("")
  const [posting, setPosting] = useState(false)
  const [vehCompany, setVehCompany] = useState("") // company filter for the vehicle picker ("" = all)
  const [postAllOpen, setPostAllOpen] = useState(false)
  const [postAllLedger, setPostAllLedger] = useState("")
  const [postingAll, setPostingAll] = useState(false)

  const load = async () => {
    try {
      setLoading(true)
      const r = await getFleetFuelReport({ month })
      setData(r?.data || { rows: [], logs: [], summary: {} })
    } catch (e) { toastError(e?.message || "Failed to load fuel report") } finally { setLoading(false) }
  }
  useEffect(() => { load() /* eslint-disable-next-line */ }, [month])
  useEffect(() => { (async () => {
    try { const r = await getVehicles({ status: 1 }); setVehicles(r?.data || []) } catch (e) {}
    try { const r = await getFleetPaymentLedgers(); setPayLedgers(r?.data || []) } catch (e) {}
  })() }, [])

  const s = data.summary || {}
  const rows = data.rows || []
  const logs = data.logs || []
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const vehLabel = v => `${(v.plate_code ? v.plate_code + "-" : "")}${v.plate_no}${v.name_of_vehicle ? ` · ${v.name_of_vehicle}` : ""}`

  // companies that own vehicles (for the vehicle-picker filter) + the searchable options
  const companies = useMemo(() => {
    const m = new Map()
    vehicles.forEach(v => {
      if (v.owner_company_id && !m.has(v.owner_company_id))
        m.set(v.owner_company_id, v.owner?.code || v.owner?.company_name || `Company ${v.owner_company_id}`)
    })
    return [...m.entries()].map(([id, name]) => ({ id, name }))
  }, [vehicles])
  const vehicleOptions = useMemo(
    () => vehicles
      .filter(v => !vehCompany || String(v.owner_company_id) === String(vehCompany))
      .map(v => ({ value: v.id, label: vehLabel(v) })),
    [vehicles, vehCompany]
  )

  const openAdd = () => { setForm(blankForm()); setModal(true) }
  const openEdit = l => { setForm({ id: l.id, vehicle_id: String(l.vehicle_id), date: String(l.date).slice(0, 10), km: l.km ?? "", fuel_qty: l.fuel_qty ?? "", amount: l.amount ?? "", fuel_type: l.fuel_type || "diesel", odometer: l.odometer ?? "", station: l.station || "", driver_name: l.driver_name || "", remarks: l.remarks || "", payment_ledger_id: "" }); setModal(true) }
  const pickVehicle = id => {
    const v = vehicles.find(x => String(x.id) === String(id))
    const heavy = ["BUS", "COASTER", "MINI BUS"].includes(String(v?.vehicle_type).toUpperCase())
    setForm(f => ({ ...f, vehicle_id: id, driver_name: f.driver_name || v?.driver_name || "", fuel_type: heavy ? "diesel" : "petrol" }))
  }
  const save = async () => {
    if (!form.vehicle_id) return toastError("Vehicle is required")
    try { setSaving(true); const r = await saveFleetFuelLog(form); toastSuccess(r?.message || "Fuel entry saved"); setModal(false); await load() }
    catch (e) { toastError(e?.message || "Save failed") } finally { setSaving(false) }
  }
  const openPost = l => { setPostFor(l); setPostLedger("") }
  const doPost = async () => {
    if (!postLedger) return toastError("Choose a bank/cash ledger")
    try { setPosting(true); const r = await postFleetFuelVoucher(postFor.id, postLedger); toastSuccess(r?.message || "Voucher posted"); setPostFor(null); await load() }
    catch (e) { toastError(e?.message || "Could not post voucher") } finally { setPosting(false) }
  }
  const unpostedCount = logs.filter(l => !l.voucher_ref).length
  const doPostAll = async () => {
    if (!postAllLedger) return toastError("Choose a bank/cash ledger")
    try { setPostingAll(true); const r = await postAllFleetFuelVouchers(postAllLedger, month); toastSuccess(r?.message || "Vouchers posted"); setPostAllOpen(false); setPostAllLedger(""); await load() }
    catch (e) { toastError(e?.message || "Could not post vouchers") } finally { setPostingAll(false) }
  }
  const remove = async l => { if (!window.confirm(`Remove fuel entry for ${l.plate}?`)) return; try { await removeFleetFuelLog(l.id); toastSuccess("Removed"); await load() } catch (e) { toastError(e?.message) } }

  const Kpi = ({ label, value, sub, fg, tint }) => (
    <Col md sm={4} xs={6}>
      <div style={{ border: "1px solid #eef0f4", borderRadius: 10, padding: "12px 14px", background: tint || "#fff", height: "100%" }}>
        <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase" }}>{label}</div>
        <div className="fw-bold" style={{ fontSize: 20, color: fg }}>{value}</div>
        {sub && <div className="text-muted" style={{ fontSize: 11 }}>{sub}</div>}
      </div>
    </Col>
  )

  return (
    <div className="page-content tnt-scope">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Breadcrumbs title="Fleet" breadcrumbObject={[{ title: "Fleet", link: "/fleet-dashboard" }, { title: "Fuel Consumption", link: "/fleet-fuel" }]} />
          <div className="d-flex align-items-center gap-2">
            <Input type="month" bsSize="sm" style={{ width: 160 }} value={month} onChange={e => setMonth(e.target.value)} />
            {unpostedCount > 0 && (
              <Button size="sm" color="success" outline onClick={() => { setPostAllLedger(""); setPostAllOpen(true) }}>
                <i className="bx bx-receipt me-1" /> Post all ({unpostedCount})
              </Button>
            )}
            <Button size="sm" color="primary" onClick={openAdd}><i className="bx bx-plus me-1" /> Fuel Entry</Button>
          </div>
        </div>

        <Row className="g-2 mb-3">
          <Kpi label="Total KM" value={num(s.km)} fg="#1f2937" />
          <Kpi label="Fuel Qty (L)" value={num(s.qty)} fg="#0ea5e9" tint="#f0f9ff" />
          <Kpi label="Amount (AED)" value={money(s.amount)} fg="#0f766e" tint="#f0fdfa" />
          <Kpi label="Avg Mileage" value={s.mileage != null ? `${s.mileage} km/L` : "—"} fg="#16a34a" tint="#f0fdf4" />
          <Kpi label="Cost / KM" value={s.cost_per_km != null ? money(s.cost_per_km) : "—"} sub="AED" fg="#b45309" tint="#fffbeb" />
          <Kpi label="Cost / Litre" value={s.cost_per_litre != null ? money(s.cost_per_litre) : "—"} sub="AED" fg="#7c3aed" tint="#faf5ff" />
        </Row>

        <Row className="g-3 mb-1">
          <Col md={6}>
            <Card className="mb-3"><CardBody style={{ minHeight: 120 }}>
              <h6 className="fw-semibold mb-2">Petrol vs Diesel</h6>
              <Table borderless className="mb-0" style={{ fontSize: 13 }}>
                <thead><tr><th>Fuel</th><th className="text-end">Qty (L)</th><th className="text-end">Amount (AED)</th></tr></thead>
                <tbody>
                  <tr><td>Petrol</td><td className="text-end">{num(s.fuel?.petrol?.qty)}</td><td className="text-end">{money(s.fuel?.petrol?.amount)}</td></tr>
                  <tr><td>Diesel</td><td className="text-end">{num(s.fuel?.diesel?.qty)}</td><td className="text-end">{money(s.fuel?.diesel?.amount)}</td></tr>
                </tbody>
              </Table>
            </CardBody></Card>
          </Col>
          <Col md={6}>
            <Card className="mb-3"><CardBody style={{ minHeight: 120 }}>
              <h6 className="fw-semibold mb-2">Heavy vs Light</h6>
              <Table borderless className="mb-0" style={{ fontSize: 13 }}>
                <thead><tr><th>Class</th><th className="text-end">KM</th><th className="text-end">Qty (L)</th><th className="text-end">Amount</th><th className="text-end">Mileage</th></tr></thead>
                <tbody>
                  <tr><td>Heavy (Bus/Coaster)</td><td className="text-end">{num(s.heavy?.km)}</td><td className="text-end">{num(s.heavy?.qty)}</td><td className="text-end">{money(s.heavy?.amount)}</td><td className="text-end">{s.heavy?.mileage != null ? `${s.heavy.mileage}` : "—"}</td></tr>
                  <tr><td>Light (Car/Van)</td><td className="text-end">{num(s.light?.km)}</td><td className="text-end">{num(s.light?.qty)}</td><td className="text-end">{money(s.light?.amount)}</td><td className="text-end">{s.light?.mileage != null ? `${s.light.mileage}` : "—"}</td></tr>
                </tbody>
              </Table>
            </CardBody></Card>
          </Col>
        </Row>

        <Card className="mb-3"><CardBody>
          <h6 className="fw-semibold mb-2">Consumption by Vehicle</h6>
          <div style={{ overflowX: "auto" }}>
            <Table responsive borderless className="align-middle tnt-table mb-0" style={{ fontSize: 13, minWidth: 820 }}>
              <thead>
                <tr>
                  <th style={{ width: 45 }}>#</th><th>Vehicle</th><th>Type</th>
                  <th className="text-end">KM</th><th className="text-end">Fuel Qty (L)</th>
                  <th className="text-end">Amount (AED)</th><th className="text-end">Mileage (km/L)</th><th className="text-end">Cost / KM</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan="8" className="text-center py-4"><Spinner size="sm" /> Loading…</td></tr>}
                {!loading && !rows.length && <tr><td colSpan="8" className="text-center text-muted py-4">No fuel entries for {month}. Click “Fuel Entry”.</td></tr>}
                {!loading && rows.map((r, i) => (
                  <tr key={r.vehicle_id}>
                    <td className="text-muted">{i + 1}</td>
                    <td className="fw-500">{r.plate}<div className="text-muted" style={{ fontSize: 11 }}>{r.vehicle}</div></td>
                    <td>{r.vehicle_type}</td>
                    <td className="text-end">{num(r.km)}</td>
                    <td className="text-end">{num(r.qty)}</td>
                    <td className="text-end">{money(r.amount)}</td>
                    <td className="text-end fw-600" style={{ color: r.mileage >= 5 ? "#16a34a" : "#d97706" }}>{r.mileage != null ? r.mileage : "—"}</td>
                    <td className="text-end">{r.cost_per_km != null ? money(r.cost_per_km) : "—"}</td>
                  </tr>
                ))}
                {!loading && !!rows.length && (
                  <tr style={{ borderTop: "2px solid #e5e7eb" }}>
                    <td className="fw-600" colSpan="3">Total ({s.vehicles})</td>
                    <td className="text-end fw-600">{num(s.km)}</td>
                    <td className="text-end fw-600">{num(s.qty)}</td>
                    <td className="text-end fw-600">{money(s.amount)}</td>
                    <td className="text-end fw-600">{s.mileage}</td>
                    <td className="text-end fw-600">{money(s.cost_per_km)}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </CardBody></Card>

        <Card className="mb-3"><CardBody>
          <h6 className="fw-semibold mb-2">Fuel Entries</h6>
          <div style={{ overflowX: "auto" }}>
            <Table responsive borderless className="align-middle tnt-table mb-0" style={{ fontSize: 13, minWidth: 820 }}>
              <thead>
                <tr><th style={{ width: 95 }}>Date</th><th>Vehicle</th><th>Fuel</th><th className="text-end">KM</th><th className="text-end">Qty (L)</th><th className="text-end">Amount</th><th>Station</th><th>Voucher</th><th className="text-end">Action</th></tr>
              </thead>
              <tbody>
                {!logs.length && <tr><td colSpan="9" className="text-center text-muted py-3">No entries.</td></tr>}
                {logs.map(l => (
                  <tr key={l.id}>
                    <td className="text-nowrap">{fmtDate(l.date)}</td>
                    <td className="fw-500">{l.plate}</td>
                    <td className="text-capitalize">{l.fuel_type}</td>
                    <td className="text-end">{num(l.km)}</td>
                    <td className="text-end">{num(l.fuel_qty)}</td>
                    <td className="text-end">{money(l.amount)}</td>
                    <td>{l.station || "—"}</td>
                    <td className="text-nowrap">
                      {l.voucher_ref
                        ? <span className="badge bg-soft-success text-success">{l.voucher_ref}</span>
                        : <Button size="sm" color="link" className="p-0" onClick={() => openPost(l)}><i className="bx bx-receipt me-1" />Post</Button>}
                    </td>
                    <td className="text-end text-nowrap">
                      <Button size="sm" color="link" className="p-0 me-2" onClick={() => openEdit(l)}><i className="bx bx-edit" /></Button>
                      <Button size="sm" color="link" className="p-0 text-danger" onClick={() => remove(l)}><i className="bx bx-trash" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody></Card>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered>
        <ModalHeader toggle={() => setModal(false)}>{form.id ? "Edit" : "New"} Fuel Entry</ModalHeader>
        <ModalBody>
          <Row className="g-2">
            {companies.length > 1 && (
              <Col md={4}><FormGroup><Label>Company</Label>
                <Input type="select" bsSize="sm" value={vehCompany} onChange={e => setVehCompany(e.target.value)}>
                  <option value="">All companies</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Input></FormGroup></Col>
            )}
            <Col md={companies.length > 1 ? 8 : 12}><FormGroup><Label>Vehicle<span className="text-danger">*</span></Label>
              <Select
                classNamePrefix="rs" isClearable isSearchable placeholder="Search & select vehicle…"
                options={vehicleOptions}
                value={vehicleOptions.find(o => String(o.value) === String(form.vehicle_id)) || null}
                onChange={opt => pickVehicle(opt?.value || "")}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </FormGroup></Col>
            <Col md={6}><FormGroup><Label>Date</Label><Input type="date" bsSize="sm" value={form.date} onChange={e => set("date", e.target.value)} /></FormGroup></Col>
            <Col md={4}><FormGroup><Label>KM run</Label><Input type="number" min="0" step="0.01" bsSize="sm" value={form.km} onChange={e => set("km", e.target.value)} /></FormGroup></Col>
            <Col md={4}><FormGroup><Label>Fuel Qty (L)</Label><Input type="number" min="0" step="0.01" bsSize="sm" value={form.fuel_qty} onChange={e => set("fuel_qty", e.target.value)} /></FormGroup></Col>
            <Col md={4}><FormGroup><Label>Amount (AED)</Label><Input type="number" min="0" step="0.01" bsSize="sm" value={form.amount} onChange={e => set("amount", e.target.value)} /></FormGroup></Col>
            {/* Fuel type is derived from the vehicle (heavy → diesel, else → petrol), no manual pick. */}
            <Col md={4}><FormGroup><Label>Odometer</Label><Input type="number" min="0" step="0.01" bsSize="sm" value={form.odometer} onChange={e => set("odometer", e.target.value)} /></FormGroup></Col>
            <Col md={4}><FormGroup><Label>Station</Label><Input bsSize="sm" placeholder="ENOC / ADNOC" value={form.station} onChange={e => set("station", e.target.value)} /></FormGroup></Col>
            {!form.id && (
              <Col md={12}><FormGroup>
                <Label>Pay from <span className="text-muted" style={{ fontWeight: 400 }}>(optional — auto-posts the payment voucher)</span></Label>
                <Input type="select" bsSize="sm" value={form.payment_ledger_id} onChange={e => set("payment_ledger_id", e.target.value)}>
                  <option value="">Don’t post a voucher (record fuel only)</option>
                  {payLedgers.some(l => l.kind === "bank") && (
                    <optgroup label="Bank">
                      {payLedgers.filter(l => l.kind === "bank").map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </optgroup>
                  )}
                  {payLedgers.some(l => l.kind === "cash") && (
                    <optgroup label="Cash">
                      {payLedgers.filter(l => l.kind === "cash").map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </optgroup>
                  )}
                </Input>
              </FormGroup></Col>
            )}
            <Col md={12}><FormGroup><Label>Remarks</Label><Input bsSize="sm" value={form.remarks} onChange={e => set("remarks", e.target.value)} /></FormGroup></Col>
          </Row>
          {form.km && form.fuel_qty ? <div className="text-muted small">Mileage: <b>{(Number(form.km) / Number(form.fuel_qty)).toFixed(2)} km/L</b></div> : null}
        </ModalBody>
        <ModalFooter>
          <Button color="light" size="sm" onClick={() => setModal(false)}>Cancel</Button>
          <Button color="primary" size="sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </ModalFooter>
      </Modal>

      {/* 2nd way: post the payment voucher for an already-saved fuel entry */}
      <Modal isOpen={!!postFor} toggle={() => setPostFor(null)} centered>
        <ModalHeader toggle={() => setPostFor(null)}>Post Payment Voucher</ModalHeader>
        <ModalBody>
          {postFor && (
            <>
              <div className="mb-2 small">
                <b>{postFor.plate}</b> · {fmtDate(postFor.date)} · <b>{money(postFor.amount)} AED</b>
              </div>
              <FormGroup>
                <Label>Pay from</Label>
                <Input type="select" bsSize="sm" value={postLedger} onChange={e => setPostLedger(e.target.value)}>
                  <option value="">Select bank/cash ledger</option>
                  {payLedgers.some(l => l.kind === "bank") && (
                    <optgroup label="Bank">
                      {payLedgers.filter(l => l.kind === "bank").map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </optgroup>
                  )}
                  {payLedgers.some(l => l.kind === "cash") && (
                    <optgroup label="Cash">
                      {payLedgers.filter(l => l.kind === "cash").map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </optgroup>
                  )}
                </Input>
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" size="sm" onClick={() => setPostFor(null)}>Cancel</Button>
          <Button color="primary" size="sm" onClick={doPost} disabled={posting || !postLedger}>{posting ? "Posting…" : "Post voucher"}</Button>
        </ModalFooter>
      </Modal>

      {/* Post vouchers for all unposted entries in the current month */}
      <Modal isOpen={postAllOpen} toggle={() => setPostAllOpen(false)} centered>
        <ModalHeader toggle={() => setPostAllOpen(false)}>Post All Unposted Vouchers</ModalHeader>
        <ModalBody>
          <div className="mb-2 small text-muted">
            Posts a payment voucher for the <b>{unpostedCount}</b> entr{unpostedCount === 1 ? "y" : "ies"} without one in <b>{month}</b>, all from the ledger you pick.
          </div>
          <FormGroup>
            <Label>Pay from</Label>
            <Input type="select" bsSize="sm" value={postAllLedger} onChange={e => setPostAllLedger(e.target.value)}>
              <option value="">Select bank/cash ledger</option>
              {payLedgers.some(l => l.kind === "bank") && (
                <optgroup label="Bank">
                  {payLedgers.filter(l => l.kind === "bank").map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </optgroup>
              )}
              {payLedgers.some(l => l.kind === "cash") && (
                <optgroup label="Cash">
                  {payLedgers.filter(l => l.kind === "cash").map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </optgroup>
              )}
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" size="sm" onClick={() => setPostAllOpen(false)}>Cancel</Button>
          <Button color="primary" size="sm" onClick={doPostAll} disabled={postingAll || !postAllLedger}>{postingAll ? "Posting…" : `Post ${unpostedCount}`}</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default FleetFuel
